import { Moon01Icon } from '@repo/studio-design-system-ui-icons/weather/moon-01'
import { SunIcon } from '@repo/studio-design-system-ui-icons/weather/sun'
import * as React from 'react'
import { IconButton } from 'primitives/buttons/icon-button'
import { useHydrated } from 'hooks/use-hydrated'
import { useDarkMode } from '../../../context/dark-mode/dark-mode.context'
import { IconHolder } from '@repo/studio-components-icon-holder'
import { tw } from 'utils/classnames'
import { useTranslation } from 'react-i18next'

/*
 * `DarkModeProvider`'s `useState` initializer reads localStorage + system
 * matchMedia synchronously. On the server those are unavailable, so `preference`
 * is `'light'` (fallback); on the client the real stored value is read during
 * hydration. Branching the rendered SVG on that value produces a hydration
 * mismatch on the `<path d="..." />` attribute (Sun vs Moon).
 *
 * `useHydrated()` is a `useSyncExternalStore` hook that returns `false` on the
 * server and during the very first client render, then `true` thereafter —
 * the React-canonical way to gate preference-dependent UI past the hydration
 * boundary. We render the Moon-icon fallback (and no `title`) until hydrated,
 * then re-render with the real preference. The icon swap after hydration is a
 * normal state update, not a mismatch. `onClick` stays wired the whole time.
 *
 * Components that mount *after* the initial hydration (e.g. opened sidebars,
 * lazy routes) skip the fallback entirely: `useHydrated()` already returns
 * `true` on their first render.
 */
export function DarkModeSwitch({
  className,
  size = 'large',
}: React.ComponentProps<'button'> & { size?: 'large' | 'small' }) {
  const { preference, togglePreference, isForced } = useDarkMode()
  const { t } = useTranslation('editor')
  const hydrated = useHydrated()
  const isLight = hydrated && preference === 'light'

  if (isForced) {
    return null
  }

  return (
    <IconButton
      title={
        hydrated
          ? isLight
            ? t('studio.editor.settings.switch_to_dark')
            : t('studio.editor.settings.switch_to_light')
          : undefined
      }
      onClick={togglePreference}
      variant="transparent"
      aria-label="toggle theme"
      className={tw('items-center justify-center hidden sm:flex', className)}
    >
      <IconHolder size={size}>{isLight ? <SunIcon /> : <Moon01Icon />}</IconHolder>
    </IconButton>
  )
}
